<template>
  <div class="draggable-layout">
    <div
      v-if="position !== 'top'"
      class="_top"
    >
      <slot />
    </div>
    <div
      ref="divider"
      class="_divider"
    >
      <div
        :class="['_border', { '-dragging': dragging }]"
        @mousedown="handleDragStart"
      >
        <div class="_border-inner" />
      </div>
      <m-btn-group
        class="_toggle-group"
      >
        <m-btn
          fab
          icon="toggle-center"
          :color="position === 'center' ? 'primary' : 'default'"
          @click="updatePosition('center')"
        />
        <m-btn
          fab
          icon="toggle-top"
          :color="position === 'top' ? 'primary' : 'default'"
          @click="updatePosition('top')"
        />
        <m-btn
          fab
          icon="toggle-bottom"
          :color="position === 'bottom' ? 'primary' : 'default'"
          @click="updatePosition('bottom')"
        />
      </m-btn-group>
    </div>
    <div
      v-if="position !== 'bottom'"
      :class="['_bottom', `-${position}`]"
      :style="{ height: `${draggingHeight}px` }"
    >
      <slot name="bottom" />
    </div>
  </div>
</template>

<script setup>
import useDebounce from 'shared/composables/debounce';
import { computed, onMounted, onUnmounted, ref } from 'vue';

defineProps({
  position: {
    type: String,
    required: true,
    validator: (value) => ['top', 'center', 'bottom'].includes(value),
  },
});

const emit = defineEmits(['update:position']);

const divider = ref(null);
const draggingHeight = ref(window.innerHeight * 0.4);
const dragging = ref(false);
const draggingStartY = ref(0);
const windowHeight = ref(window.innerHeight);
const minHeight = 200;
const maxHeight = computed(() => windowHeight.value - 350);

const debounceSvc = useDebounce();

const updatePosition = (pos) => {
  emit('update:position', pos);
};

const setHeight = (value) => {
  const height = Math.max(minHeight, Math.min(value, maxHeight.value));
  draggingHeight.value = height;
};

const setStoredHeight = () => {
  localStorage.setItem('chart_editor_draggingHeight', draggingHeight.value);
};

const handleDragStart = (event) => {
  event.stopPropagation();
  event.preventDefault();

  document.body.classList.add('no-interaction');

  window.addEventListener('mousemove', handleDrag);
  window.addEventListener('mouseup', handleDragEnd);
  const d = divider.value.getBoundingClientRect();
  setHeight(windowHeight.value - d.top - d.height);
  dragging.value = true;
  draggingStartY.value = event.clientY;
};

const handleDragEnd = () => {
  window.removeEventListener('mousemove', handleDrag);
  window.removeEventListener('mouseup', handleDragEnd);
  document.body.classList.remove('no-interaction');

  dragging.value = false;
  setStoredHeight();
};

const handleDrag = (event) => {
  event.preventDefault();
  event.stopPropagation();

  if (!dragging.value) {
    return;
  }

  const newVal = draggingHeight.value - (event.clientY - draggingStartY.value);
  if (newVal < minHeight || newVal > maxHeight.value) {
    return;
  }

  const update = () => {
    draggingHeight.value = newVal;
    draggingStartY.value = event.clientY;
  };

  debounceSvc.debounce(update, 5);
};

const handleResize = () => {
  windowHeight.value = window.innerHeight;
  setHeight(draggingHeight.value);
};

onMounted(() => {
  windowHeight.value = window.innerHeight;
  const storedHeight = localStorage.getItem('chart_editor_draggingHeight');
  if (storedHeight) {
    setHeight(parseInt(storedHeight, 10));
  }
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleDrag);
  window.removeEventListener('mouseup', handleDragEnd);
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped lang="scss">
.draggable-layout {
  overflow: auto;
  display: grid;
  grid-template-rows: 1fr 4rem auto;

  &.-top {
    grid-template-rows: 4rem auto;
  }

  &.-bottom {
    grid-template-rows: auto 4rem;
  }

  ._top {
    min-height: 20rem;
  }

  ._divider {
    height: 4rem;
    position: relative;

    ._border {
      cursor: ns-resize;
      height: .6rem;
      position: absolute;
      top: 1.7rem;
      left: 0;
      right: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      ._border-inner {
        background-color: $input-border-color;
        height: 1px;
        width: 100%;
      }

      &:hover, &.-dragging {
        ._border-inner {
          height: 2px;
          background-color: $primary-color;
        }
      }
    }

    ._toggle-group {
      position: absolute;
      top: .4rem;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1;
      background-color: white;
    }
  }

  ._bottom {
    overflow: auto;
  }
}
</style>
